import React, { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useRequest } from "ahooks";
import { GlobalHelper } from "../../../../../../utils/helpers/global-helper";
import { selectCandidateProfile } from "../../../../../auth/redux/selector";
import { fetchPrudnaAssessments } from "../../../../network/network";
import { convertAssessments } from "../prudna-widget/prudna-widget.util";
import { ONBOARDING_PATH, ROOT_PATH } from "../../../../../../routes/constants";
import { IconFont } from "../../../../../../icon-font/iconfont";
import { textStyleSet } from "../../../../../../mui-theme/text.style";
import { IconFontLayer } from "../../../../../../icon-font/iconfont-layer";
import { ConfigList } from "../prudna-widget/prudna-widget.component";
import {
  PHKCandidateLanding,
  PHKCandidateLandingScreenName,
  PHKLCandidateGAFeature,
  PHKLCandidateGAModule,
  PHKLOBJECT_TYPE,
} from "../../../../ga/types/phkl-ga-parameter-type";
import { takePHKLGAUIClickEvent } from "../../../../ga/ga";
import { getTargetCandidate } from "../../../../utils";

type ComponentProps = {};

export const PHKLPrudnaSectionComponent: React.FC<ComponentProps> = memo(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const candidateId = GlobalHelper.getTokenUserSub();
  const candidateProfile = useSelector(selectCandidateProfile);
  const isCandidateActive = candidateProfile?.status === "IN_PROGRESS";

  const { data } = useRequest(() => fetchPrudnaAssessments(candidateId));

  const assessmentData = useMemo(() => {
    if (!data?.data?.[0]) return null;

    return convertAssessments({
      assessment: data.data[0],
      isCandidateActive,
    });
  }, [data, isCandidateActive, i18n.language]);

  if (!assessmentData) return null;

  const { title, message, time, leftConfig, rightConfig, buttons } = assessmentData;

  const hasButtons = !!buttons?.length;

  const clickMore = () => {
    const candidate = getTargetCandidate();
    takePHKLGAUIClickEvent({
      module: PHKLCandidateGAModule,
      feature: PHKLCandidateGAFeature,
      journey: PHKCandidateLanding,
      stage: "view_prudna_session",
      screen_name: PHKCandidateLandingScreenName,
      object_name: "more",
      object_type: PHKLOBJECT_TYPE.BUTTON,
      item_value: candidate?.candidateCode,
      item_value_2: candidate?.agentid,
    });

    navigate(`${ROOT_PATH}/${ONBOARDING_PATH}/prudna-history`);
  };

  return (
    <div className="px-3 py-2 md:px-0">
      <div className="bg-white rounded-2xl">
        {/* Header */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200">
          <p className="flex-1 text-[20px] font-semibold text-[#1A1A1A] leading-[28px]">
            {t("Common.prudna")}
          </p>

          {/* Navigate to history */}
          <button
            className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-gray-600"
            onClick={clickMore}>
            {t("Common.more")}
            <IconFont name="arrow_right_24" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-4">
          <div className="flex md:flex-row md:items-start md:gap-4">
            {/* Left info section */}
            <div className="flex flex-1">
              {/* icon */}
              <div className="w-[56px] mr-3 flex-shrink-0 flex items-start">
                <IconFontLayer
                  size={36}
                  backgroundSize={56}
                  containerStyle={{
                    borderRadius: 12,
                    borderWidth: 0,
                    justifySelf: "center",
                    backgroundColor: "#F3EBFF",
                  }}
                  color="#A574F3"
                  name="prudna"
                />
              </div>

              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-[17px] font-semibold leading-[24px]">{title}</span>

                {/*  content */}
                {time && <InfoRow icon="time" text={time} />}
                {message && <InfoRow icon="feedback" text={message} />}
              </div>
            </div>

            {/* Desktop buttons */}
            {hasButtons && (
              <div className="hidden md:flex flex-col gap-3 items-start">
                {buttons.map(({ text, onPress }, index) => (
                  <button
                    key={text + index}
                    className="flex items-center justify-center w-[105px] h-9 rounded text-sm font-semibold text-white bg-[#E8192C] hover:bg-[#d32f2f]"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPress?.();
                    }}>
                    {t(text)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Strengths & growth (only when no message) */}
          {message ? null : (
            <StrengthsAndGrowth leftConfig={leftConfig} rightConfig={rightConfig} t={t} />
          )}

          {/* Mobile buttons */}
          {hasButtons && (
            <div className="flex gap-2 mt-6 w-full justify-end md:hidden">
              {buttons.map(({ text, onPress }, index) => (
                <button
                  key={text + index}
                  className="flex items-center justify-center w-full h-9 rounded text-sm font-semibold text-white bg-[#E8192C] hover:bg-[#d32f2f]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPress?.();
                  }}>
                  {t(text)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

/** Simple row: icon + text */
const InfoRow: React.FC<{ icon?: string; text: React.ReactNode }> = ({ icon, text }) => (
  <div className="flex items-start gap-1 mt-1 text-grey33" style={textStyleSet.text_16_med}>
    {icon && (
      <div className="shrink-0">
        <IconFont name={icon} size={16} />
      </div>
    )}
    <span className="break-words">{text}</span>
  </div>
);

const StrengthsAndGrowth: FC<{
  leftConfig?: any[];
  rightConfig?: any[];
  t: (key: string) => any;
}> = ({ leftConfig, rightConfig, t }) => {
  const hasLeftConfig = !!leftConfig?.length;
  const hasRightConfig = !!rightConfig?.length;

  // No data
  if (!hasLeftConfig && !hasRightConfig) return null;

  return (
    <div className="flex flex-row flex-1 mt-5">
      <ConfigList title={t("Recruitment.your_strengths")} type="left" items={leftConfig} />
      {hasLeftConfig && hasRightConfig && <div className="w-[2px] bg-[#E6E6E6] h-auto mx-3 " />}
      <ConfigList title={t("Recruitment.your_areas_for_growth")} type="right" items={rightConfig} />
      {(!hasLeftConfig || !hasRightConfig) && <div className="flex-1 ml-6" />}
    </div>
  );
};
